import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  BadgeCheck,
  ChevronDown,
  ChevronRight,
  Download,
  Loader2,
  Search,
  ShieldCheck,
  Undo2,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { Modal } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import type { Ciclo, FacturaPrevista, VistaPrevia } from './contrato';

const dinero = (valor: number) =>
  `$${valor.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dia = (iso: string) => iso.split('-').reverse().join('/');

/**
 * Las facturas tal como saldrían, y el visto bueno de quien supervisa la emisión. Sin él vigente
 * no se factura; si los pesajes cambian después de darlo, deja de valer.
 */
export function EmisionSupervisada({ ciclo, alCambiar }: { ciclo: Ciclo; alCambiar: () => void }) {
  const { puede } = useSesion();
  const [buscar, setBuscar] = useState('');
  const [abiertas, setAbiertas] = useState<Set<number>>(new Set());
  const [confirmando, setConfirmando] = useState(false);
  const [bajando, setBajando] = useState(false);
  const aprueba = puede('generadores.aprobar_emision');

  const prevista = useQuery({
    queryKey: ['generadores', 'vista-previa', ciclo.id, ciclo.sinFacturar, ciclo.revisadoEn],
    queryFn: () => api.get<VistaPrevia>(`/generadores/ciclos/${ciclo.id}/vista-previa`),
  });

  const darlo = useMutation({
    mutationFn: () => api.post<Ciclo>(`/generadores/ciclos/${ciclo.id}/visto-bueno`),
    onSuccess: () => {
      notificar.exito('Visto bueno dado', 'Ya se puede facturar');
      setConfirmando(false);
      alCambiar();
    },
    onError: (causa) =>
      notificar.error(
        causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO DAR EL VISTO BUENO',
      ),
  });

  const quitarlo = useMutation({
    mutationFn: () => api.delete<Ciclo>(`/generadores/ciclos/${ciclo.id}/visto-bueno`),
    onSuccess: () => {
      notificar.exito('Visto bueno retirado', 'Hay que revisarlo de nuevo antes de facturar');
      alCambiar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO RETIRAR'),
  });

  async function bajarExcel() {
    setBajando(true);
    try {
      const excel = await api.descargar(`/generadores/ciclos/${ciclo.id}/vista-previa.xlsx`);
      if (!excel) {
        notificar.error('NO SE PUDO ARMAR EL EXCEL');
        return;
      }
      const url = URL.createObjectURL(excel);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = `vista-previa-generadores-${ciclo.periodo}.xlsx`;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      URL.revokeObjectURL(url);
    } catch (causa) {
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO DESCARGAR');
    } finally {
      setBajando(false);
    }
  }

  const p = prevista.data;
  const texto = buscar.trim().toUpperCase();
  const facturas =
    p?.facturas.filter(
      (f) =>
        !texto ||
        f.generador.toUpperCase().includes(texto) ||
        f.identificacion.includes(texto) ||
        (f.sucursal ?? '').includes(texto),
    ) ?? [];

  function alternar(id: number) {
    setAbiertas((antes) => {
      const despues = new Set(antes);
      if (despues.has(id)) despues.delete(id);
      else despues.add(id);
      return despues;
    });
  }

  return (
    <section className="mb-5 grid gap-4" aria-label="Emisión supervisada">
      <EstadoDelVistoBueno ciclo={ciclo} />

      <div className="flex flex-wrap items-center gap-2">
        {p && (
          <>
            <Cifra valor={String(p.facturas.length)} rotulo="Facturas" />
            <Cifra valor={String(p.pesajes)} rotulo="Pesajes" />
            <Cifra valor={dinero(p.total)} rotulo="Total" />
            <span className="text-[10px] uppercase leading-relaxed text-armazon-500">
              Emisión {dia(p.emision)}
              <br />
              Vencen {dia(p.vencimiento)}
            </span>
          </>
        )}
        <span className="ml-auto flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void bajarExcel()}
            disabled={bajando}
            className={BOTON_SECUNDARIO}
          >
            {bajando ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Download className="size-4" aria-hidden />
            )}
            Excel
          </button>
          {aprueba && ciclo.vistoBuenoVigente && (
            <button
              type="button"
              onClick={() => quitarlo.mutate()}
              disabled={quitarlo.isPending}
              className={BOTON_SECUNDARIO}
            >
              <Undo2 className="size-4" aria-hidden />
              Retirar visto bueno
            </button>
          )}
          {aprueba && !ciclo.vistoBuenoVigente && (
            <button
              type="button"
              onClick={() => setConfirmando(true)}
              disabled={!p || p.facturas.length === 0 || !p.cuadra}
              className={BOTON_PRINCIPAL}
            >
              <ShieldCheck className="size-4" aria-hidden />
              Dar visto bueno
            </button>
          )}
        </span>
      </div>

      {p && !p.cuadra && (
        <p
          role="alert"
          className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs font-semibold uppercase text-rose-800"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
          Las facturas suman {dinero(p.total)} y los pesajes {dinero(p.totalDePesajes)}: no cuadra.
          Avise a sistemas antes de facturar.
        </p>
      )}

      <div className="rounded-2xl border border-armazon-200 bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-armazon-100 px-4 py-3">
          <h2 className="text-xs font-bold uppercase tracking-wide text-armazon-700">
            Vista previa · las facturas tal como saldrían
          </h2>
          <label className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-armazon-400"
              aria-hidden
            />
            <input
              value={buscar}
              onChange={(e) => setBuscar(e.target.value)}
              placeholder="Generador, RUC o sucursal…"
              aria-label="Buscar en la vista previa"
              className="h-9 w-60 rounded-lg border border-armazon-200 pl-8 pr-2 text-xs"
            />
          </label>
        </div>
        {prevista.isPending ? (
          <p className="flex items-center justify-center gap-2 p-10 text-xs uppercase text-armazon-500">
            <Loader2 className="size-4 animate-spin" aria-hidden /> Armando la vista previa…
          </p>
        ) : (
          <div className="max-h-[60dvh] overflow-auto">
            <table className="w-full min-w-[720px] text-left text-xs">
              <thead className="sticky top-0 bg-armazon-50 text-[10px] uppercase tracking-wide text-armazon-500">
                <tr>
                  <th className="w-8 px-3 py-2" />
                  <th className="px-3 py-2">Generador</th>
                  <th className="px-3 py-2">RUC</th>
                  <th className="px-3 py-2 text-right">Pesajes</th>
                  <th className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-armazon-100">
                {facturas.map((f) => (
                  <FilaDeFactura
                    key={f.generadorId}
                    factura={f}
                    abierta={abiertas.has(f.generadorId)}
                    alAlternar={() => alternar(f.generadorId)}
                  />
                ))}
                {facturas.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center uppercase text-armazon-400">
                      {texto ? 'Ninguna factura coincide' : 'No hay nada por facturar'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {confirmando && p && (
        <Modal
          abierto
          alCerrar={() => setConfirmando(false)}
          titulo="Dar el visto bueno"
          subtitulo={`La emisión de generadores de ${ciclo.periodo}`}
          icono={ShieldCheck}
          ancho="medio"
        >
          <div className="grid gap-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <Cifra valor={String(p.facturas.length)} rotulo="Facturas" />
              <Cifra valor={String(p.pesajes)} rotulo="Pesajes" />
              <Cifra valor={dinero(p.total)} rotulo="Total" />
            </div>
            <p className="text-[11px] uppercase leading-relaxed text-armazon-500">
              Con su visto bueno se podrá facturar exactamente esto. Si después se corrige, añade o
              borra un pesaje, el visto bueno deja de valer y hay que darlo de nuevo. Queda
              registrado en la auditoría con su nombre.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmando(false)}
                className={BOTON_SECUNDARIO}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => darlo.mutate()}
                disabled={darlo.isPending}
                className={BOTON_PRINCIPAL}
              >
                {darlo.isPending ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : (
                  <BadgeCheck className="size-4" aria-hidden />
                )}
                Doy el visto bueno
              </button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}

function EstadoDelVistoBueno({ ciclo }: { ciclo: Ciclo }) {
  if (ciclo.vistoBuenoVigente) {
    return (
      <p
        role="status"
        className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs font-semibold uppercase text-emerald-900"
      >
        <BadgeCheck className="size-5 shrink-0 text-emerald-600" aria-hidden />
        Visto bueno de {ciclo.revisadoPor}
        {ciclo.revisadoEn && ` el ${new Date(ciclo.revisadoEn).toLocaleString('es-EC')}`}: ya se
        puede facturar
      </p>
    );
  }
  if (ciclo.revisadoPor) {
    return (
      <p
        role="status"
        className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-semibold uppercase text-amber-900"
      >
        <AlertTriangle className="size-5 shrink-0 text-amber-600" aria-hidden />
        El visto bueno de {ciclo.revisadoPor} ya no vale: los pesajes cambiaron después. Hay que
        revisar la vista previa de nuevo.
      </p>
    );
  }
  return (
    <p
      role="status"
      className="flex items-center gap-2 rounded-xl border border-armazon-200 bg-armazon-50 p-3 text-xs font-semibold uppercase text-armazon-700"
    >
      <ShieldCheck className="size-5 shrink-0 text-armazon-400" aria-hidden />
      Pendiente del visto bueno de quien supervisa la emisión. Revise la vista previa: sin visto
      bueno no se factura.
    </p>
  );
}

function FilaDeFactura({
  factura,
  abierta,
  alAlternar,
}: {
  factura: FacturaPrevista;
  abierta: boolean;
  alAlternar: () => void;
}) {
  return (
    <>
      <tr className="cursor-pointer hover:bg-armazon-50/70" onClick={alAlternar}>
        <td className="px-3 py-2 text-armazon-400">
          <button
            type="button"
            aria-expanded={abierta}
            aria-label={abierta ? 'Ocultar las líneas' : 'Ver las líneas'}
            onClick={(e) => {
              e.stopPropagation();
              alAlternar();
            }}
          >
            {abierta ? (
              <ChevronDown className="size-4" aria-hidden />
            ) : (
              <ChevronRight className="size-4" aria-hidden />
            )}
          </button>
        </td>
        <td className="px-3 py-2">
          <span className="block font-semibold uppercase text-armazon-800">
            {factura.generador}
          </span>
          {factura.sucursal && (
            <span className="block text-[10px] font-semibold uppercase text-marca-700">
              Sucursal · {factura.sucursal}
            </span>
          )}
        </td>
        <td className="importe px-3 py-2 text-armazon-500">{factura.identificacion}</td>
        <td className="importe px-3 py-2 text-right text-armazon-600">{factura.pesajes}</td>
        <td className="importe px-3 py-2 text-right font-bold text-armazon-900">
          {dinero(factura.total)}
        </td>
      </tr>
      {abierta && (
        <tr className="bg-armazon-50/60">
          <td />
          <td colSpan={4} className="px-3 pb-3">
            <table className="w-full text-[11px]">
              <thead className="text-[9px] uppercase tracking-wide text-armazon-400">
                <tr>
                  <th className="py-1 text-left">Detalle</th>
                  <th className="py-1 text-right">Cantidad</th>
                  <th className="py-1 text-right">P. unitario</th>
                  <th className="py-1 text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {factura.lineas.map((l, i) => (
                  <tr key={i} className="border-t border-armazon-100">
                    <td className="py-1 pr-3 uppercase text-armazon-700">{l.descripcion}</td>
                    <td className="importe py-1 text-right">
                      {l.cantidad.toLocaleString('es-EC', { maximumFractionDigits: 3 })} {l.unidad}
                    </td>
                    <td className="importe py-1 text-right">${l.precioUnitario.toFixed(2)}</td>
                    <td className="importe py-1 text-right font-semibold">{dinero(l.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

function Cifra({ valor, rotulo }: { valor: string; rotulo: string }) {
  return (
    <div className="rounded-xl border border-armazon-200 bg-white px-4 py-2">
      <span className="importe block text-lg font-bold text-armazon-800">{valor}</span>
      <span className="text-[10px] font-semibold uppercase text-armazon-500">{rotulo}</span>
    </div>
  );
}

const BOTON_PRINCIPAL =
  'inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white shadow-sm transition-colors hover:bg-marca-700 disabled:opacity-50';
const BOTON_SECUNDARIO =
  'inline-flex h-10 items-center gap-2 rounded-lg border border-armazon-300 bg-white px-4 text-xs font-bold uppercase text-armazon-700 shadow-sm transition-colors hover:bg-armazon-50 disabled:opacity-50';
